import { useState } from "react";
import { Briefcase, Edit3, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteExperience } from "./hooks/useExperience";
import { useCandidateExperiences } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { ExperienceFormModal } from "./components/ExperienceFormModal";
import type { ExperienceDetail } from "@/interface/profile";

export default function ProfessionalTab({
  profileId,
}: {
  profileId?: number | string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState<ExperienceDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const {
    data: candExpRes,
    isLoading: isCandLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateExperiences(targetId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteExperience();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const experienceList =
    candExpRes?.pages.flatMap((page: any) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const isLoading = isCandLoading && experienceList?.length === 0;

  const handleOpenAdd = () => {
    setSelectedExp(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (exp: ExperienceDetail) => {
    setSelectedExp(exp);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Work Experience
        </h2>
        <Button
          onClick={handleOpenAdd}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Experience
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="space-y-8 max-h-[700px] overflow-y-auto pr-2 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 dark:before:via-slate-800 before:to-transparent custom-scrollbar"
        >
          {experienceList?.map((exp: ExperienceDetail) => (
            <div
              key={exp.id}
              className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white dark:border-slate-900 bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-300 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10">
                <Briefcase className="w-4 h-4" />
              </div>

              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800 transition-all relative">
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(exp)}
                    className="p-1.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 bg-slate-50 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded transition-colors"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTargetId(String(exp.id))}
                    className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-slate-50 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-slate-900 dark:text-white text-lg">
                    {exp.role}
                  </h3>
                </div>
                <div className="text-purple-600 dark:text-purple-400 font-medium text-sm mb-3">
                  {exp.company}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-4 font-medium">
                  {(exp.start_date ||
                    exp.end_date ||
                    exp.currently_working) && (
                    <span className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800/80 px-2 py-1 rounded-md border border-slate-100 dark:border-slate-800">
                      {exp.start_date ? `${exp.start_date} - ` : ""}
                      {exp.currently_working
                        ? "Present"
                        : exp.end_date || "Present"}
                    </span>
                  )}
                  {exp.location && (
                    <span className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800/80 px-2 py-1 rounded-md border border-slate-100 dark:border-slate-800">
                      {exp.location}
                    </span>
                  )}
                </div>

                {exp.description && (
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-4 whitespace-pre-line">
                    {exp.description}
                  </p>
                )}

                {((exp.skills_used && exp.skills_used.length > 0) ||
                  (exp.technologies && exp.technologies.length > 0)) && (
                  <div className="flex flex-wrap gap-2">
                    {(exp.skills_used || exp.technologies || []).map(
                      (
                        skill:
                          | number
                          | string
                          | { id?: number; skill_name?: string },
                        idx: number,
                      ) => {
                        const name =
                          typeof skill === "object"
                            ? skill.skill_name
                            : String(skill);
                        return (
                          <span
                            key={
                              typeof skill === "object"
                                ? skill.id || idx
                                : skill
                            }
                            className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-[11px] font-medium rounded-md"
                          >
                            {name}
                          </span>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {experienceList.length === 0 && (
            <div className="text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl relative z-10 bg-white dark:bg-slate-900">
              No work experience added yet.
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-6 relative z-10">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 font-medium">
                Loading more experiences...
              </span>
            </div>
          )}
        </div>
      )}

      <ExperienceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedExp}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Work Experience"
        description="Are you sure you want to remove this experience record?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
